// JQ4-29 — US-TICKETS-009: Supervisor elapsed-time pure helpers.
//
// Design D5 — per-status timestamp anchor:
//   - parked      → checkedInAt       (dwell time on the lot)
//   - requested   → retrievalRequestedAt (how long the customer has been waiting)
//   - in_transit  → claimedAt         (how long the retrieval is in progress)
//
// All functions are pure (no side effects, no imports) and unit-tested in elapsed.test.ts.

import type { SupervisorActiveTicketDTO } from '@jq4race/shared';

// ── pickElapsedAnchor ─────────────────────────────────────────────────────────

/**
 * Returns the ISO timestamp string to use as the elapsed-time anchor for a given
 * ticket, per design D5. Never returns null — falls back to checkedInAt.
 *
 * Anchor rules (design D5):
 *   parked      → checkedInAt
 *   requested   → retrievalRequestedAt (fallback: checkedInAt)
 *   in_transit  → claimedAt            (fallback: retrievalRequestedAt → checkedInAt)
 */
export function pickElapsedAnchor(
  ticket: Pick<
    SupervisorActiveTicketDTO,
    'status' | 'checkedInAt' | 'retrievalRequestedAt' | 'claimedAt'
  >,
): string {
  switch (ticket.status) {
    case 'parked':
      return ticket.checkedInAt;

    case 'requested':
      return ticket.retrievalRequestedAt ?? ticket.checkedInAt;

    case 'in_transit':
      return ticket.claimedAt ?? ticket.retrievalRequestedAt ?? ticket.checkedInAt;
  }
}

// ── formatElapsed ─────────────────────────────────────────────────────────────

/**
 * Formats a duration in seconds as a human-readable string.
 *
 * Format:
 *   - < 1 hour  → "m:ss"   e.g. "0:00", "9:59", "59:59"
 *   - ≥ 1 hour  → "h:mm:ss" e.g. "1:00:00", "1:01:01"
 *
 * Negative values are clamped to 0 (handles minor clock skew).
 */
export function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));

  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  const ss = s.toString().padStart(2, '0');
  const mm = m.toString().padStart(2, '0');

  if (h > 0) {
    return `${h}:${mm}:${ss}`;
  }

  // Under 1 hour: "m:ss" — minutes are NOT zero-padded
  return `${m}:${ss}`;
}
